import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { expedientesAPI, tiposAPI, partesPreestablecidasAPI } from '../../services/api';
import { fechaLimiteDesdeInput } from '../../utils/fechaCalendario';
import { validateGoogleMapsUrl } from '../../utils/validations';
import { esTipoEMS } from '../../utils/expedienteTipos';
import {
  TIPO_PROYECTO_OPTIONS,
  DEPARTAMENTOS_PERU,
  emptyCatalogoFields,
  validateCatalogoFields,
  catalogoPayloadFromForm,
  requiereCatalogoCampos,
  tipoProyectoFromApi,
} from '../../utils/expedienteCatalogo';
import { generarDireccionCompleta } from '../../utils/expedienteDireccion';
import ClienteCombobox from '../ClienteCombobox';
import ClienteDniFirst from './ClienteDniFirst';
import './expediente-form.css';

const STEPS = [
  { id: 1, label: 'Datos' },
  { id: 2, label: 'Ubicación' },
];

const baseFormState = () => ({
  numero: '',
  clienteId: '',
  tipoId: '',
  montoTotal: '',
  fechaLimite: '',
  ubicacionUrl: '',
  fechaCreacion: '',
  direccionCompleta: '',
  calle: '',
  manzana: '',
  lote: '',
  lugar: '',
  ...emptyCatalogoFields(),
  contratoPlazoDias: '',
  contratoLugarFirma: '',
  contratoFechaFirma: '',
  numeroPisos: '',
});

const ExpedienteFormWizard = ({ mode = 'create', expedienteId = null }) => {
  const navigate = useNavigate();
  const isEdit = mode === 'edit';
  const montoTotalInputRef = useRef(null);
  const clienteDraftRef = useRef({ dni: '', nombre: '', celular: '', mode: 'dni' });

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [tipos, setTipos] = useState([]);
  const [partesOpcionales, setPartesOpcionales] = useState([]);
  const [partesSeleccionadas, setPartesSeleccionadas] = useState({});
  const [fechaLimiteEditable, setFechaLimiteEditable] = useState(isEdit);
  const [showFechaAntigua, setShowFechaAntigua] = useState(false);
  const [createdAt, setCreatedAt] = useState(null);
  const [formData, setFormData] = useState(baseFormState);

  const esEMS = esTipoEMS(formData.tipoId);
  const tipoSeleccionado = tipos.find((t) => t.id === parseInt(formData.tipoId, 10));
  const esPlanosConstruccion = String(tipoSeleccionado?.nombre || '').toUpperCase().includes('PLANO DE CONSTRUCCION');
  const catalogoRequired = requiereCatalogoCampos(createdAt, { isCreate: !isEdit });

  useEffect(() => {
    if (loading) return;
    const el = montoTotalInputRef.current;
    if (!el) return;
    const onWheel = (e) => e.preventDefault();
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [loading, step]);

  useEffect(() => {
    const load = async () => {
      try {
        if (isEdit) {
          const [expRes, tiposRes] = await Promise.all([
            expedientesAPI.getExpediente(expedienteId),
            tiposAPI.getTipos(),
          ]);
          const exp = expRes.data;
          setCreatedAt(exp.createdAt);
          setTipos(tiposRes.data);
          setFormData({
            ...baseFormState(),
            numero: exp.numero || '',
            clienteId: exp.clienteId?.toString() || '',
            tipoId: exp.tipoId?.toString() || '',
            fechaLimite: exp.fechaLimite
              ? new Date(exp.fechaLimite).toISOString().split('T')[0]
              : '',
            montoTotal: exp.montoTotal?.toString() || '',
            ubicacionUrl: exp.ubicacionUrl || '',
            direccionCompleta: exp.direccionCompleta || '',
            calle: exp.calle || '',
            manzana: exp.manzana || '',
            lote: exp.lote || '',
            lugar: exp.lugar || '',
            sector: exp.sector || exp.lugar || '',
            distrito: exp.distrito || '',
            departamento: exp.departamento || '',
            tipoProyecto: tipoProyectoFromApi(exp.tipoProyecto),
            tipoProyectoOtro: exp.tipoProyectoOtro || '',
            metrosCuadrados: exp.metrosCuadrados != null ? String(exp.metrosCuadrados) : '',
            numeroPisos: exp.numeroPisos != null ? String(exp.numeroPisos) : '',
            contratoPlazoDias: exp.contratoConstruccion?.plazoDias != null ? String(exp.contratoConstruccion.plazoDias) : '',
            contratoLugarFirma: exp.contratoConstruccion?.lugarFirma || '',
            contratoFechaFirma: exp.contratoConstruccion?.fechaFirma ? new Date(exp.contratoConstruccion.fechaFirma).toISOString().split('T')[0] : '',
          });
        } else {
          const tiposRes = await tiposAPI.getTipos();
          setTipos(tiposRes.data);
          const hoy = new Date().toISOString().split('T')[0];
          setFormData((prev) => ({ ...prev, fechaCreacion: hoy }));
        }
      } catch (err) {
        setError(err.message || 'Error al cargar datos');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isEdit, expedienteId]);

  useEffect(() => {
    if (isEdit || !formData.fechaCreacion || !formData.tipoId) return;
    const fechaCreacion = new Date(formData.fechaCreacion);
    const fechaLimite = new Date(fechaCreacion);
    const tipoSeleccionado = tipos.find((t) => t.id === parseInt(formData.tipoId, 10));
    const diasLimite = tipoSeleccionado?.diasLimite || 15;
    fechaLimite.setDate(fechaLimite.getDate() + diasLimite);
    setFormData((prev) => ({
      ...prev,
      fechaLimite: fechaLimite.toISOString().split('T')[0],
    }));
  }, [formData.fechaCreacion, formData.tipoId, tipos, isEdit]);

  useEffect(() => {
    if (!formData.tipoId || isEdit) {
      if (!formData.tipoId) {
        setPartesOpcionales([]);
        setPartesSeleccionadas({});
      }
      return;
    }
    const loadPartes = async () => {
      try {
        const response = await partesPreestablecidasAPI.getPartesByTipo(formData.tipoId);
        const opcionales = response.data.filter((parte) => {
          if (!parte.es_opcional) return false;
          const d = String(parte.descripcion || '').trim().toLowerCase();
          return d !== 'gas';
        });
        setPartesOpcionales(opcionales);
      } catch {
        setPartesOpcionales([]);
      }
    };
    loadPartes();
  }, [formData.tipoId, isEdit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const direccionPreview = generarDireccionCompleta({
    manzana: formData.manzana,
    lote: formData.lote,
    sector: formData.sector,
    distrito: formData.distrito,
    departamento: formData.departamento,
  });

  const handleClienteDraftChange = useCallback((draft) => {
    clienteDraftRef.current = draft;
  }, []);

  const ensureClienteId = async () => {
    if (formData.clienteId) return parseInt(formData.clienteId, 10);

    const { dni, nombre, celular, mode } = clienteDraftRef.current;
    if (mode === 'search') {
      throw new Error('Seleccione un cliente de la lista');
    }

    try {
      const res = await expedientesAPI.createCliente({
        dni,
        nombre: nombre.trim(),
        celular: celular?.trim() || null,
      });
      const id = String(res.data.id);
      setFormData((prev) => ({ ...prev, clienteId: id }));
      return res.data.id;
    } catch (err) {
      const msg = err.response?.data?.error || err.message || '';
      if (String(msg).toLowerCase().includes('dni')) {
        const lookup = await expedientesAPI.buscarDNI(dni);
        if (lookup.data?.success && lookup.data.data?.id) {
          const id = String(lookup.data.data.id);
          setFormData((prev) => ({ ...prev, clienteId: id }));
          return lookup.data.data.id;
        }
      }
      throw new Error(msg || 'Error al registrar cliente');
    }
  };

  const validateClienteDraft = () => {
    if (formData.clienteId) return null;
    const { dni, nombre, celular, mode } = clienteDraftRef.current;
    if (mode === 'search') return 'Seleccione un cliente de la lista';
    if (!/^\d{8}$/.test(dni)) return 'Ingrese un DNI válido de 8 dígitos';
    if (!nombre?.trim()) return 'Ingrese el nombre del cliente';
    if (celular && celular.length !== 9) return 'El celular debe tener 9 dígitos';
    return null;
  };

  const validateStep1 = () => {
    if (isEdit && !formData.numero?.trim()) return 'Número de expediente es obligatorio';
    if (isEdit && !formData.clienteId) return 'Seleccione un cliente';
    if (!isEdit) {
      const clienteErr = validateClienteDraft();
      if (clienteErr) return clienteErr;
    }
    if (!formData.tipoId) return 'Seleccione un tipo de trabajo';
    if (!formData.montoTotal || parseFloat(formData.montoTotal) <= 0) {
      return 'El monto total debe ser mayor a 0';
    }
    if (!formData.fechaLimite) return 'Fecha límite es obligatoria';
    if (esPlanosConstruccion) {
      if (!Number.isInteger(Number(formData.numeroPisos)) || Number(formData.numeroPisos) <= 0) return 'Indique la cantidad de pisos';
      if (!Number.isInteger(Number(formData.contratoPlazoDias)) || Number(formData.contratoPlazoDias) <= 0 || Number(formData.contratoPlazoDias) > 365) return 'Indique un plazo de elaboración entre 1 y 365 días';
      if (!formData.contratoLugarFirma.trim()) return 'Indique el lugar de firma del contrato';
      if (!formData.contratoFechaFirma) return 'Indique la fecha de firma del contrato';
    }
    return null;
  };

  const validateStep2 = () => {
    const mapsErr = validateGoogleMapsUrl(formData.ubicacionUrl, { required: esEMS });
    if (mapsErr) return mapsErr;
    if (catalogoRequired) {
      return validateCatalogoFields(formData, { required: true });
    }
    if (formData.sector || formData.distrito || formData.tipoProyecto || formData.metrosCuadrados) {
      return validateCatalogoFields(formData, { required: true });
    }
    return null;
  };

  const goNext = async () => {
    setError(null);
    const err = validateStep1();
    if (err) {
      setError(err);
      return;
    }
    if (!isEdit) {
      try {
        await ensureClienteId();
      } catch (e) {
        setError(e.message);
        return;
      }
    }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const err1 = validateStep1();
    if (err1) {
      setError(err1);
      setStep(1);
      return;
    }
    const err2 = validateStep2();
    if (err2) {
      setError(err2);
      return;
    }

    setSaving(true);
    try {
      let clienteIdFinal = formData.clienteId;
      if (!isEdit) {
        clienteIdFinal = await ensureClienteId();
      }

      const catalogo = catalogoPayloadFromForm(formData);
      const payload = {
        clienteId: parseInt(clienteIdFinal, 10),
        tipoId: parseInt(formData.tipoId, 10),
        montoTotal: parseFloat(formData.montoTotal),
        fechaLimite: fechaLimiteDesdeInput(formData.fechaLimite),
        ubicacionUrl: formData.ubicacionUrl || undefined,
        direccionCompleta: formData.direccionCompleta || undefined,
        calle: formData.calle || undefined,
        manzana: formData.manzana || undefined,
        lote: formData.lote || undefined,
        lugar: formData.lugar || undefined,
        ...catalogo,
      };
      if (esPlanosConstruccion) {
        payload.numeroPisos = Number(formData.numeroPisos);
        payload.contrato = { plazoDias: Number(formData.contratoPlazoDias), lugarFirma: formData.contratoLugarFirma.trim(), fechaFirma: formData.contratoFechaFirma };
      }

      if (isEdit) {
        payload.numero = formData.numero;
        await expedientesAPI.updateExpediente(expedienteId, payload);
        navigate(`/expedientes/${expedienteId}`);
      } else {
        payload.fechaCreacion = formData.fechaCreacion
          ? new Date(formData.fechaCreacion).toISOString()
          : undefined;
        payload.partesOpcionales = Object.keys(partesSeleccionadas)
          .filter((id) => partesSeleccionadas[id])
          .map((id) => parseInt(id, 10));
        await expedientesAPI.createExpediente(payload);
        navigate('/expedientes');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Error al guardar';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const cancel = useCallback(() => {
    if (isEdit) navigate(`/expedientes/${expedienteId}`);
    else navigate('/expedientes');
  }, [isEdit, expedienteId, navigate]);

  if (loading) {
    return <div className="expediente-form-loading">Cargando…</div>;
  }

  const title = isEdit ? `Editar ${formData.numero}` : 'Nuevo expediente';

  return (
    <div className="expediente-form-page">
      <div className="expediente-form-sheet">
        <h1 className="expediente-form-title">{title}</h1>

        <div className="expediente-form-stepper">
          {STEPS.map((s) => (
            <div
              key={s.id}
              className={[
                'expediente-form-step',
                step === s.id ? 'is-active' : '',
                step > s.id ? 'is-done' : '',
              ].filter(Boolean).join(' ')}
            >
              <span className="expediente-form-step-dot">{s.id}</span>
              <span className="expediente-form-step-label">{s.label}</span>
            </div>
          ))}
        </div>

        {error && <div className="expediente-form-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          {step === 1 && (
            <>
              {isEdit && (
                <div className="expediente-form-field">
                  <label htmlFor="numero">Número *</label>
                  <input
                    id="numero"
                    name="numero"
                    value={formData.numero}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              )}

              {!isEdit ? (
                <ClienteDniFirst
                  clienteId={formData.clienteId}
                  onClienteIdChange={(id) => setFormData((prev) => ({ ...prev, clienteId: id }))}
                  onDraftChange={handleClienteDraftChange}
                />
              ) : (
                <div className="expediente-form-field">
                  <label htmlFor="clienteId">Cliente *</label>
                  <ClienteCombobox
                    value={formData.clienteId}
                    onChange={handleInputChange}
                    required
                    allowCreate={false}
                  />
                </div>
              )}

              <div className="expediente-form-field">
                <label htmlFor="tipoId">Tipo de trabajo *</label>
                <select
                  id="tipoId"
                  name="tipoId"
                  value={formData.tipoId}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Seleccionar</option>
                  {tipos.map((tipo) => (
                    <option key={tipo.id} value={tipo.id}>{tipo.nombre}</option>
                  ))}
                </select>
              </div>

              {!isEdit && partesOpcionales.length > 0 && (
                <div className="expediente-form-field">
                  <label>Partes opcionales</label>
                  <div className="expediente-form-partes">
                    {partesOpcionales.map((parte) => (
                      <label key={parte.id} className="expediente-form-parte-chip">
                        <input
                          type="checkbox"
                          checked={partesSeleccionadas[parte.id] || false}
                          onChange={(e) => setPartesSeleccionadas((prev) => ({
                            ...prev,
                            [parte.id]: e.target.checked,
                          }))}
                        />
                        {parte.descripcion}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="expediente-form-field">
                <label htmlFor="montoTotal">Monto total *</label>
                <input
                  ref={montoTotalInputRef}
                  type="number"
                  id="montoTotal"
                  name="montoTotal"
                  value={formData.montoTotal}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  required
                />
              </div>

              {esPlanosConstruccion && (
                <div className="expediente-form-contract">
                  <div className="expediente-form-section-title">Datos del contrato</div>
                  <p className="expediente-form-help">Especialidades: Arquitectura, Estructuras, Instalaciones Sanitarias, Instalaciones Eléctricas, Gas y Vista 3D de fachada.</p>
                  <div className="expediente-form-grid-2">
                    <div className="expediente-form-field">
                      <label htmlFor="numeroPisos">Cantidad de pisos *</label>
                      <input type="number" min="1" id="numeroPisos" name="numeroPisos" value={formData.numeroPisos} onChange={handleInputChange} required />
                    </div>
                    <div className="expediente-form-field">
                      <label htmlFor="contratoPlazoDias">Plazo de elaboración (días) *</label>
                      <input type="number" min="1" max="365" id="contratoPlazoDias" name="contratoPlazoDias" value={formData.contratoPlazoDias} onChange={handleInputChange} required />
                    </div>
                    <div className="expediente-form-field">
                      <label htmlFor="contratoFechaFirma">Fecha de firma *</label>
                      <input type="date" id="contratoFechaFirma" name="contratoFechaFirma" value={formData.contratoFechaFirma} onChange={handleInputChange} required />
                    </div>
                  </div>
                  <div className="expediente-form-field">
                    <label htmlFor="contratoLugarFirma">Lugar de firma *</label>
                    <input id="contratoLugarFirma" name="contratoLugarFirma" value={formData.contratoLugarFirma} onChange={handleInputChange} required />
                  </div>
                </div>
              )}

              <div className="expediente-form-field">
                <label htmlFor="fechaLimite">Fecha límite *</label>
                <div className="expediente-form-fecha-row">
                  <input
                    type="date"
                    id="fechaLimite"
                    name="fechaLimite"
                    value={formData.fechaLimite}
                    onChange={handleInputChange}
                    disabled={!isEdit && !fechaLimiteEditable}
                    required
                  />
                  {!isEdit && (
                    <button
                      type="button"
                      className="expediente-form-fecha-toggle"
                      onClick={() => setFechaLimiteEditable((v) => !v)}
                      title={fechaLimiteEditable ? 'Bloquear' : 'Editar'}
                    >
                      {fechaLimiteEditable ? '🔒' : '✎'}
                    </button>
                  )}
                </div>
              </div>

              {!isEdit && (
                <>
                  {!showFechaAntigua ? (
                    <button
                      type="button"
                      className="expediente-form-antiguo-toggle"
                      onClick={() => setShowFechaAntigua(true)}
                    >
                      Registrar expediente antiguo
                    </button>
                  ) : (
                    <div className="expediente-form-field">
                      <label htmlFor="fechaCreacion">Fecha de creación</label>
                      <input
                        type="date"
                        id="fechaCreacion"
                        name="fechaCreacion"
                        value={formData.fechaCreacion}
                        onChange={handleInputChange}
                      />
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {step === 2 && (
            <>
              <div className="expediente-form-section-title">Ubicación</div>
              <div className="expediente-form-grid-2">
                <div className="expediente-form-field">
                  <label htmlFor="manzana">Manzana</label>
                  <input
                    id="manzana"
                    name="manzana"
                    value={formData.manzana}
                    onChange={handleInputChange}
                    placeholder="Ej. D"
                  />
                </div>
                <div className="expediente-form-field">
                  <label htmlFor="lote">Lote</label>
                  <input
                    id="lote"
                    name="lote"
                    value={formData.lote}
                    onChange={handleInputChange}
                    placeholder="Ej. 14"
                  />
                </div>
                <div className="expediente-form-field">
                  <label htmlFor="sector">Sector{catalogoRequired ? ' *' : ''}</label>
                  <input
                    id="sector"
                    name="sector"
                    value={formData.sector}
                    onChange={handleInputChange}
                    required={catalogoRequired}
                    placeholder="Ej. UNION BELLAVISTA"
                  />
                </div>
                <div className="expediente-form-field">
                  <label htmlFor="distrito">Distrito{catalogoRequired ? ' *' : ''}</label>
                  <input
                    id="distrito"
                    name="distrito"
                    value={formData.distrito}
                    onChange={handleInputChange}
                    required={catalogoRequired}
                    placeholder="Ej. SAN ANTONIO"
                  />
                </div>
                <div className="expediente-form-field">
                  <label htmlFor="departamento">Departamento{catalogoRequired ? ' *' : ''}</label>
                  <select
                    id="departamento"
                    name="departamento"
                    value={formData.departamento}
                    onChange={handleInputChange}
                    required={catalogoRequired}
                  >
                    {!catalogoRequired && <option value="">—</option>}
                    {DEPARTAMENTOS_PERU.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
                <div className="expediente-form-field">
                  <label htmlFor="metrosCuadrados">m²{catalogoRequired ? ' *' : ''}</label>
                  <input
                    type="number"
                    id="metrosCuadrados"
                    name="metrosCuadrados"
                    value={formData.metrosCuadrados}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    required={catalogoRequired}
                  />
                </div>
              </div>

              {direccionPreview && (
                <div className="expediente-form-direccion-preview">
                  <span className="expediente-form-direccion-preview-label">Dirección del expediente</span>
                  {direccionPreview}
                </div>
              )}

              <div className="expediente-form-field">
                <label htmlFor="tipoProyecto">Tipo de proyecto{catalogoRequired ? ' *' : ''}</label>
                <select
                  id="tipoProyecto"
                  name="tipoProyecto"
                  value={formData.tipoProyecto}
                  onChange={handleInputChange}
                  required={catalogoRequired}
                >
                  <option value="">Seleccionar</option>
                  {TIPO_PROYECTO_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              {formData.tipoProyecto === 'OTROS' && (
                <div className="expediente-form-field">
                  <label htmlFor="tipoProyectoOtro">Especificar *</label>
                  <input
                    id="tipoProyectoOtro"
                    name="tipoProyectoOtro"
                    value={formData.tipoProyectoOtro}
                    onChange={handleInputChange}
                    required={catalogoRequired || formData.tipoProyecto === 'OTROS'}
                  />
                </div>
              )}

              {esEMS && (
                <div className="expediente-form-field">
                  <label htmlFor="ubicacionUrl">URL Google Maps *</label>
                  <input
                    type="url"
                    id="ubicacionUrl"
                    name="ubicacionUrl"
                    value={formData.ubicacionUrl}
                    onChange={handleInputChange}
                    required
                    placeholder="https://maps.google.com/..."
                  />
                </div>
              )}
            </>
          )}

          <div className="expediente-form-actions">
            {step === 1 ? (
              <>
                <button type="button" className="expediente-form-btn expediente-form-btn-secondary" onClick={cancel}>
                  Cancelar
                </button>
                <div className="expediente-form-actions-right">
                  <button type="button" className="expediente-form-btn expediente-form-btn-primary" onClick={goNext}>
                    Siguiente
                  </button>
                </div>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="expediente-form-btn expediente-form-btn-secondary"
                  onClick={() => { setError(null); setStep(1); }}
                >
                  Atrás
                </button>
                <div className="expediente-form-actions-right">
                  <button type="button" className="expediente-form-btn expediente-form-btn-secondary" onClick={cancel} disabled={saving}>
                    Cancelar
                  </button>
                  <button type="submit" className="expediente-form-btn expediente-form-btn-primary" disabled={saving}>
                    {saving ? 'Guardando…' : isEdit ? 'Guardar' : 'Crear'}
                  </button>
                </div>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpedienteFormWizard;
